import {
  Anchor,
  Box,
  Button,
  Flex,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconPlayerPlayFilled } from "@tabler/icons-react";
import { useDemoModal } from "$/components/demo-modal";
import { DotPattern } from "$/components/dot-pattern";
import { useImageLightbox } from "$/components/image-lightbox";
import { Reveal } from "$/components/reveal";
import { Section } from "$/components/section";
import { WHATSAPP_URL } from "$/data/links";

const HERO_MOCKUP_SRC = "/hero-mockup.webp";
const HERO_MOCKUP_ALT = "Verdana rodando em um notebook";

const trustPoints = [
  "Funciona sem internet",
  "Você é dono, sem aluguel",
  "Compra única",
  "Roda no seu PC",
];

export function Hero() {
  const { open: openDemo } = useDemoModal();

  return (
    <Box pos="relative" style={{ overflow: "hidden" }}>
      <DotPattern
        id="hero-dot-pattern"
        mask="radial-gradient(ellipse 80% 70% at 50% 30%, black 20%, transparent 75%)"
        style={{ zIndex: 0 }}
      />

      <Box
        pos="absolute"
        top={-220}
        right={-120}
        w={460}
        h={460}
        bg="green.1"
        visibleFrom="md"
        style={{ borderRadius: "50%", filter: "blur(110px)", opacity: 0.5, zIndex: 0 }}
      />

      <Box pos="relative" style={{ zIndex: 1 }}>
        <Section>
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={{ base: 48, md: 60 }} verticalSpacing="xl">
            <Stack gap="lg" justify="center" className="hero-stack">
              <Reveal delay={0}>
                <Group
                  gap={10}
                  px={14}
                  py={5}
                  align="center"
                  wrap="nowrap"
                  style={{
                    width: "fit-content",
                    transform: "rotate(-1.2deg)",
                    borderRadius: 999,
                    background:
                      "linear-gradient(135deg, rgba(232, 245, 233, 0.7) 0%, rgba(232, 245, 233, 0.35) 100%)",
                    border: "1px solid rgba(43, 138, 62, 0.18)",
                    boxShadow: "0 1px 2px rgba(43, 138, 62, 0.05)",
                  }}
                >
                  <svg
                    width="18"
                    height="10"
                    viewBox="0 0 18 10"
                    aria-hidden="true"
                    style={{ flexShrink: 0, display: "block" }}
                  >
                    <title>Indicador</title>
                    <path
                      d="M 1 5.5 Q 3.2 1.5, 5.6 5.5 T 10.2 5.5 T 14.8 5.5 L 17 5.5"
                      fill="none"
                      stroke="var(--mantine-color-green-7)"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <Text fz={12.5} fw={600} c="green.9" style={{ letterSpacing: "0.005em" }}>
                    PDV completo · roda sem internet
                  </Text>
                </Group>
              </Reveal>

              <Reveal delay={100}>
                <Title
                  order={1}
                  fz={{ base: 32, md: 52 }}
                  lh={1.08}
                  fw={800}
                  style={{ letterSpacing: "-0.03em" }}
                >
                  O{" "}
                  <Text
                    component="span"
                    inherit
                    variant="gradient"
                    gradient={{ from: "green.7", to: "green.5", deg: 135 }}
                  >
                    pequeno comércio
                  </Text>{" "}
                  também merece software bem feito.
                </Title>
              </Reveal>

              <Reveal delay={200}>
                <Text fz={{ base: 16, md: 18 }} c="gray.7" lh={1.55} maw={500}>
                  O PDV que também cuida de estoque, balanço e clientes. Roda no computador da sua
                  loja, offline, e o sistema é seu. Você compra uma vez, sem aluguel.
                </Text>
              </Reveal>

              <Reveal delay={300}>
                <Flex mt="sm" gap="md" direction={{ base: "column", xs: "row" }} wrap="wrap">
                  <Button
                    size="lg"
                    px="xl"
                    variant="gradient"
                    gradient={{ from: "green.7", to: "green.5", deg: 135 }}
                    rightSection={<IconArrowRight size={20} />}
                    component="a"
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener"
                    className="cta-shift-icon"
                  >
                    Começar Agora
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    color="dark"
                    leftSection={<IconPlayerPlayFilled size={20} />}
                    onClick={openDemo}
                  >
                    Ver demonstração
                  </Button>
                </Flex>
              </Reveal>

              <Reveal delay={400}>
                <Flex
                  gap="sm"
                  mt={4}
                  direction="row"
                  display={{ base: "none", xs: "flex" }}
                  justify={{ base: "center", md: "start" }}
                  wrap="wrap"
                  maw={500}
                >
                  {trustPoints.map((p) => (
                    <Group key={p} gap={6}>
                      <ThemeIcon size={18} radius="xl" color="green" variant="filled">
                        <IconCheck size={12} />
                      </ThemeIcon>
                      <Text fz="sm" c="gray.7" fw={500}>
                        {p}
                      </Text>
                    </Group>
                  ))}
                </Flex>
              </Reveal>

              <Reveal delay={500}>
                <Anchor href="/#testimonials" underline="hover" c="gray.6" fz="sm" lh={1.5} mt={4}>
                  Já em uso em estabelecimentos de Garanhuns, PE.
                </Anchor>
              </Reveal>
            </Stack>

            <Reveal delay={200} style={{ height: "100%", display: "flex", alignItems: "center" }}>
              <HeroMockup />
            </Reveal>
          </SimpleGrid>
        </Section>
      </Box>
    </Box>
  );
}

function HeroMockup() {
  const { open: openLightbox } = useImageLightbox();

  return (
    <Box
      pos="relative"
      w="100%"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        pos="absolute"
        w="85%"
        h="65%"
        bg="green.1"
        visibleFrom="sm"
        style={{
          borderRadius: "50%",
          filter: "blur(80px)",
          opacity: 0.55,
          zIndex: 0,
          top: "18%",
          left: "8%",
        }}
      />

      <Box
        component="button"
        type="button"
        onClick={() => openLightbox({ src: HERO_MOCKUP_SRC, alt: HERO_MOCKUP_ALT })}
        aria-label="Ampliar imagem do Verdana"
        pos="relative"
        w="100%"
        style={{
          zIndex: 1,
          background: "transparent",
          border: 0,
          padding: 0,
          cursor: "zoom-in",
          display: "block",
        }}
      >
        <Image
          src={HERO_MOCKUP_SRC}
          alt={HERO_MOCKUP_ALT}
          fit="contain"
          style={{
            display: "block",
            width: "100%",
            height: "auto",
            filter:
              "drop-shadow(0 20px 30px rgba(15, 23, 42, 0.18)) drop-shadow(0 40px 60px rgba(43, 138, 62, 0.18))",
          }}
          // @ts-expect-error fetchpriority is valid HTML but not in React types yet
          fetchpriority="high"
        />
      </Box>
    </Box>
  );
}
